<script setup lang="ts">
import { pb } from "@/pocketbase";
import Title from "@/components/Title.vue";
import Heading from "@/components/Heading.vue";
import Divider from "@/components/Divider.vue";
import Content from "@/components/Content.vue";
import { getTechnologyCategoryLabelForTechnologyType, getTechnologyTypeLabel, Product, ProductTechnology, Technology, technologyTypesByCategory } from "@/model";
import { useRoute } from "vue-router";
import Heading2 from "@/components/Heading2.vue";
import { computed, ref, Ref } from "vue";
import EditProductDialog from "./TechLandscape/EditProductDialog.vue";
import Button from "@/components/Button.vue";
import Hero from "@/components/Hero.vue";

const route = useRoute();

const product: Ref<Product> = ref();
const productTechnologies: Ref<ProductTechnology[]> = ref([]);

async function loadData() {
  product.value = await pb.collection('products').getOne<Product>(route.params.id, {
    expand: 'businessUnit',
    sort: 'name',
    '$autoCancel': false,
  });

  productTechnologies.value = await pb.collection('product_technologies').getFullList<ProductTechnology>(null, {
    expand: 'technology',
    sort: 'technology.name',
    filter: `product = '${product.value.id}'`,
    '$autoCancel': false,
  });
}

await loadData();

const categories = computed(() => {
  const categories: Record<string, {
    label: string,
    types: Record<string, {
      label: string,
      productTechnologies: ProductTechnology[],
    }>,
  }> = {};

  for (const productTechnology of productTechnologies.value) {
    const technology = productTechnology.expand!.technology!;
    const categoryLabel = getTechnologyCategoryLabelForTechnologyType(technology.type);
    const typeLabel = getTechnologyTypeLabel(technology.type);

    if (!categories[categoryLabel]) {
      categories[categoryLabel] = {
        label: categoryLabel,
        types: {},
      };
    }

    if (!categories[categoryLabel].types[typeLabel]) {
      categories[categoryLabel].types[typeLabel] = {
        label: typeLabel,
        productTechnologies: [],
      };
    }

    categories[categoryLabel].types[typeLabel].productTechnologies.push(productTechnology);
  }

  return Object.values(categories).sort((a, b) => {
    // sort categories
    const a1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === a.label));
    const b1 = technologyTypesByCategory.indexOf(technologyTypesByCategory.find(({ category }) => category === b.label));

    return a1 - b1;
  }).map(category => {
    // and sort types in each category
    const sortedTypes = technologyTypesByCategory.find(({ category: cat }) => cat === category.label).types;

    return {
      ...category,
      types: Object.values(category.types).sort((a, b) => {
        return sortedTypes.indexOf(a.label) - sortedTypes.indexOf(b.label);
      }),
    };
  });
});

function getLogoUrl(technology: Technology): string {
  return pb.files.getUrl(technology, technology.logo);
}

const isEditing: Ref<bool> = ref(false);
</script>

<template>
  <Title :subtitle="product.expand!.businessUnit!.name">{{ product.name }}</Title>
  <Divider/>
  <Content>
    <template v-if="isEditing">
      <EditProductDialog :product="product" @close="isEditing = false; loadData();"/>
    </template>

    <div class="flex gap-4 items-center justify-center">
      <div class="inline-block w-72">
        <Button label="Manage technologies" outline icon="edit" @click="isEditing = true;"/>
      </div>
      <div class="inline-block w-72">
        <router-link :to="{ name: 'product_print', params: { id: route.params.id }}" target="_blank">
          <Button label="Generate image" outline icon="print"/>
        </router-link>
      </div>
    </div>

    <Hero v-if="product.description.length > 0">
      {{ product.description }}
    </Hero>

    <template v-for="(category in categories">
      <Heading>{{ category.label }}</Heading>

      <div v-for="(type, idx) in category.types" :class="[
          'flex flex-col sm:flex-row',
           idx > 0 ? 'border-t border-gray-200 dark:border-gray-700 pt-4' : '',
      ]">
        <div class="sm:basis-1/6 sm:pr-10 pb-2">
          <Heading2>{{ type.label }}</Heading2>
        </div>
        <div class="sm:basis-5/6">
          <div class="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-9 gap-3">
            <div v-for="productTechnology in type.productTechnologies"
                 class="col-span-3 p-2 sm:p-4 h-40 sm:h-32 rounded-lg border border-gray-300 dark:border-gray-600 flex justify-center">

              <div class="flex items-center justify-center w-full">
                <div class="w-32 flex flex-col items-center justify-center">
                  <template v-if="productTechnology.expand!.technology.simpleicon">
                    <img :src="`https://cdn.simpleicons.org/${productTechnology.expand!.technology.simpleicon}`"
                         :alt="`Logo of ${productTechnology.expand!.technology.name}`"
                         class="mb-2 w-12 h-12 object-contain"/>
                  </template>
                  <template v-else>
                    <img v-if="productTechnology.expand!.technology.logo" :src="getLogoUrl(productTechnology.expand!.technology)"
                         :alt="`Logo of ${productTechnology.expand!.technology.name}`"
                         class="mb-1 w-10 h-10 object-contain"/>
                  </template>
                </div>

                <div class="w-full text-xs">
                  <router-link :to="{ name: 'technology', params: { id: productTechnology.technology }}">
                    <h3 class="font-semibold text-gray-500 dark:text-white text-sm mb-2">
                      {{ productTechnology.expand!.technology.name }}
                      <span class="font-thin" v-if="productTechnology.version">{{ productTechnology.version }}</span>
                    </h3>
                  </router-link>

                  <p class="mb-2">{{ productTechnology.comment || 'no comment' }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Content>
</template>

